/**
 * 売却査定モーダルフォーム
 * - MicroModal でモーダルを開閉
 * - zip → 1(物件種別) → 2(ご状況) → 3(お客様情報) のステップ切り替え
 * - ページ内 CTA（郵便番号入力済み）から開いた場合は zip ステップをスキップし、
 *   入力値を hidden の zip に埋め込む
 * - フローティングボタンから開いた場合は zip ステップから開始
 * - 各ステップ全項目必須。バリデーションは HTML5 ネイティブ（required / pattern / type）に任せ、
 *   「次へ」では reportValidity() でネイティブの吹き出しを表示する
 *   （zip ステップの入力のみ name なしの UI 専用フィールドのため JS でチェック）
 */

import MicroModal from "micromodal";

const MODAL_ID = "modal-assessment";
const VIDEO_MODAL_ID = "modal-soh-video";

/**
 * Sales of Home 紹介動画モーダル
 * - バナー（.js-soh-video-open）クリックで MicroModal を開く
 * - 開いたとき data-src を iframe の src にセットして再生開始（autoplay）
 * - 閉じたとき src を外して再生を止める
 */
const videoModal = () => {
    const root = document.getElementById(VIDEO_MODAL_ID);
    if (!root) return;

    const iframe = root.querySelector(".js-soh-video-iframe");

    document.querySelectorAll(".js-soh-video-open").forEach((btn) => {
        btn.addEventListener("click", () => {
            MicroModal.show(VIDEO_MODAL_ID, {
                disableScroll: true,
                onShow: () => {
                    iframe.src = iframe.dataset.src;
                },
                onClose: () => {
                    iframe.removeAttribute("src");
                },
            });
        });
    });
};

const assessmentModal = () => {
    const root = document.getElementById(MODAL_ID);
    if (!root) return;

    const form      = root.querySelector(".js-assessment-form");
    const zipHidden = root.querySelector(".js-assessment-zip");
    const flow      = root.querySelector(".js-assessment-flow");
    const note      = root.querySelector(".js-assessment-note");
    const dots      = Array.from(root.querySelectorAll(".js-assessment-flow-dot"));
    const steps     = Array.from(root.querySelectorAll(".js-assessment-step"));

    // フローティング起点（zip ステップ経由）で開いたかどうか
    let hasZipStep = false;
    // 現在のステップ（"zip" | "1" | "2" | "3"）
    let current = "1";

    const stepEl = (key) => steps.find((el) => el.dataset.step === key);

    // 指定ステップを表示する
    const show = (key) => {
        current = key;
        steps.forEach((el) => el.toggleAttribute("data-hidden", el.dataset.step !== key));

        // インジケーター・注記は zip ステップでは非表示
        const isZip = key === "zip";
        flow?.toggleAttribute("data-hidden", isZip);
        note?.toggleAttribute("data-hidden", isZip);

        // 現在ステップまでのドットを塗りつぶす
        if (!isZip) {
            const num = Number(key);
            dots.forEach((dot) => {
                dot.toggleAttribute("data-filled", Number(dot.dataset.index) <= num);
            });
        }

        root.querySelector(".pg-sellconsultation-modal__container")?.scrollTo(0, 0);
    };

    /**
     * モーダルを開く
     * @param {string|null} zip 入力済みの郵便番号。null のときは zip ステップから開始
     */
    const open = (zip) => {
        form.reset();
        hasZipStep = zip === null;
        zipHidden.value = zip ?? "";
        show(hasZipStep ? "zip" : "1");

        MicroModal.show(MODAL_ID, {
            disableScroll: true,
        });
    };

    const back = () => {
        switch (current) {
            case "3":
                show("2");
                break;
            case "2":
                show("1");
                break;
            case "1":
                if (hasZipStep) {
                    show("zip");
                } else {
                    MicroModal.close(MODAL_ID);
                }
                break;
            default:
                MicroModal.close(MODAL_ID);
        }
    };

    const next = () => {
        if (current === "1") {
            const radio = form.querySelector('input[name="estate_type"]');
            if (!radio.checkValidity()) {
                radio.reportValidity();
                return;
            }
            show("2");
            return;
        }
        if (current === "2") {
            const radio = form.querySelector('input[name="status"]');
            if (!radio.checkValidity()) {
                radio.reportValidity();
                return;
            }
            show("3");
        }
    };

    // 戻る / 次へ
    root.querySelectorAll(".js-assessment-back").forEach((btn) => {
        btn.addEventListener("click", back);
    });
    root.querySelectorAll(".js-assessment-next").forEach((btn) => {
        btn.addEventListener("click", next);
    });

    // zip ステップ内の CTA ボタン（入力して step1 へ）
    const zipStep = stepEl("zip");
    zipStep?.querySelector(".c-cards-assessment-cta__btn")?.addEventListener("click", () => {
        const input = zipStep.querySelector(".c-cards-assessment-cta__input");
        const zip = input?.value.trim() ?? "";
        if (zip === "") {
            alert("郵便番号を入力してください");
            input?.focus();
            return;
        }
        zipHidden.value = zip;
        show("1");
    });

    // 送信（STEP3）: バリデーションは HTML5 ネイティブに任せる
    // （invalid のときはブラウザが送信を止めるので submit イベント自体が発火しない）
    form.addEventListener("submit", () => {
        // 二重送信防止
        form.querySelector('button[type="submit"]')?.setAttribute("disabled", "");
    });

    // ページ内 CTA カード（モーダル外）: 郵便番号必須 → 入力値を埋め込んで step1 から開く
    document.querySelectorAll(".c-cards-assessment-cta").forEach((cta) => {
        if (root.contains(cta)) return;

        cta.querySelector(".c-cards-assessment-cta__btn")?.addEventListener("click", () => {
            const input = cta.querySelector(".c-cards-assessment-cta__input");
            const zip = input?.value.trim() ?? "";
            if (zip === "") {
                alert("郵便番号を入力してください");
                input?.focus();
                return;
            }
            open(zip);
        });
    });

    // フローティングボタン・ページ内CTAボタン: zip ステップから開く（JS 無効時はリンク遷移のまま）
    document.querySelectorAll(".pg-sellconsultation-floating__btn, .js-assessment-open").forEach((btn) => {
        btn.addEventListener("click", (e) => {
            e.preventDefault();
            open(null);
        });
    });

    // サーバ側エラー（reCAPTCHA 失敗・メール送信失敗等）で戻された場合は alert で通知
    if (root.dataset.serverError) {
        alert(root.dataset.serverError);
    }
};

const main = () => {
    assessmentModal();
    videoModal();
};

export default main;